// Handlinger: `legalActions` lister alt, hvad en spiller kan gøre lige nu, og `apply` udfører én intent.
// Begge bruger den samme `check`, så hver kandidat kan apply'es, og intet ulovligt slipper igennem.

import { getLevel } from '../content/levels';
import { clockFull } from './cities';
import { addCrew, crewOnLine, dispatch, MAX_CREWS, nearestIdle } from './crews';
import { bothPrice, earlyBonusPct, yearendAutoTicks } from './economy';
import { creditDuo, duoWants } from './duo';
import { forgetSeen } from './events';
import { hzBlocked, trimHz } from './hz';
import { markDirty } from './flow';
import {
  allowedMedia,
  BATTERY_COST,
  BATTERY_MW,
  BATTERY_SECONDS,
  BIG_BATTERY_MW,
  buryCost,
  cutNodes,
  checkNewLine,
  degree,
  CREW_COST,
  demolishRefund,
  FLYWHEEL_COST,
  lineById,
  nodeById,
  pairKey,
  REINFORCE_COST,
  stationCost,
  UNDO_TICKS,
  updateShields,
  upgradeCost,
} from './grid';
import { POWER_IDS, norwayLanding, powerKind, powerOf, powerReady, sheddable, usePower } from './powers';
import { packDecider, VOTE_TICKS } from './roles';
import { answerCard, CARD_COST, cardOpen, isShooable, shoo } from './threats';
import { pickPack } from './year';
import type {
  Line,
  ActionKind,
  ApplyError,
  ApplyResult,
  Candidate,
  CardOption,
  GridNode,
  Intent,
  LevelDef,
  LineMedium,
  Mechanic,
  PlayerId,
  SimEvent,
  State,
} from './types';
import { TICK_HZ, YEAR_TICKS } from './types';

type Check = { ok: true; cost: number } | { ok: false; error: ApplyError };

/** Målet for "Forstærk alle udsatte": alle luftledninger i et stormspor på én gang. */
export const REINFORCE_ALL = '*';

/** Luftledninger i sporet af en varslet storm, som endnu ikke er forstærket. */
export function exposedLines(state: State): Line[] {
  const ids = new Set<string>();
  for (const t of state.threats) if (t.kind === 'storm' && t.phase === 'warning') for (const id of t.atRisk) ids.add(id);
  return state.lines.filter((l) => ids.has(l.id) && l.medium === 'air' && l.status === 'ok' && !l.reinforced);
}

const BUILD_PHASES: State['phase'][] = ['playing'];

function has(level: LevelDef, m: Mechanic): boolean {
  return level.mechanics.includes(m);
}

function mediumMechanic(m: LineMedium): Mechanic {
  return m === 'air' ? 'air' : m === 'cable' ? 'cable' : 'sea';
}

function fail(error: ApplyError): Check {
  return { ok: false, error };
}

function afford(state: State, cost: number): Check {
  return cost <= state.budget ? { ok: true, cost } : fail('too_poor');
}

function splitPair(target: string | undefined): [string, string] | null {
  if (!target) return null;
  const parts = target.split('|');
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
  return [parts[0], parts[1]];
}

function storageNode(n: GridNode): boolean {
  return n.active && !n.phantom && (n.kind === 'city' || n.kind === 'station' || n.kind === 'wind' || n.kind === 'solar');
}

/** En ledning til et fantom giver halvdelen tilbage, rundet ned. */
export function phantomRefund(cost: number): number {
  return Math.floor(cost / 2);
}

/** Ingen mørk by, intet mørkeur over halvt og ingen knækkede ledninger. */
export function calmGrid(state: State): boolean {
  const full = clockFull(state);
  for (const n of state.nodes) {
    const c = n.city;
    if (!c || !n.active || n.phantom) continue;
    if (c.out || c.darkTicks > 0) return false;
    if (c.clock * 1000 > full * 500) return false;
  }
  return !state.lines.some((l) => l.status === 'broken' || l.halfDown);
}

/**
 * Er årets planlagte indhold spillet? Ingen hændelser i kø, ingen steder og ingen nye kunder fra
 * køen, der dukker op inden årsskiftet, og alle årets byer har fået strøm.
 */
export function yearDone(state: State): boolean {
  if (state.director.planned.length > 0) return false;
  const end = (Math.floor(state.clockTick / YEAR_TICKS) + 1) * YEAR_TICKS;
  if (state.pendingSites.some((s) => s.at < end)) return false;
  if (state.pool.length > 0 && state.nextCustomerAt < end) return false;
  return !state.nodes.some((n) => n.city && n.active && !n.phantom && n.city.demandMw > 0 && n.city.servedMw <= 0);
}

/**
 * Er handlingen lovlig nu for `player`, og hvad koster den? Rører ikke state. Budget og evner er
 * fælles i co-op; kun nedrivning er personlig (man river ikke makkerens ledning ned).
 */
export function check(
  state: State,
  kind: ActionKind,
  target: string | undefined,
  opt: string | undefined,
  player: PlayerId = 'p1',
): Check {
  const level = getLevel(state.levelId);
  if (state.phase === 'won' || state.phase === 'lost') return fail('wrong_phase');
  if (kind === 'wait') return { ok: true, cost: 0 };
  if (kind === 'pick_pack') {
    if (state.phase !== 'yearend') return fail('wrong_phase');
    const i = Number(opt);
    if (!Number.isInteger(i) || i < 0 || i >= state.packOffer.length) return fail('bad_option');
    return { ok: true, cost: 0 };
  }
  if (kind === 'pick_both') {
    if (state.phase !== 'yearend') return fail('wrong_phase');
    if (state.packOffer.length < 2) return fail('not_allowed');
    return afford(state, bothPrice(state.year));
  }
  if (!BUILD_PHASES.includes(state.phase)) return fail('wrong_phase');

  switch (kind) {
    case 'build_line': {
      const pair = splitPair(target);
      if (!pair) return fail('unknown_target');
      const medium = opt as LineMedium;
      if (medium !== 'air' && medium !== 'cable' && medium !== 'sea') return fail('bad_option');
      const r = checkNewLine(state, pair[0], pair[1], medium, (m) => has(level, mediumMechanic(m)));
      if (!r.ok) return fail(r.error);
      return afford(state, r.cost);
    }
    case 'upgrade_line': {
      const line = target ? lineById(state, target) : undefined;
      if (!line) return fail('unknown_target');
      if (line.status !== 'ok' || line.halfDown) return fail('busy');
      const cost = upgradeCost(state, line);
      if (cost === null) return fail('not_allowed');
      const mech: Mechanic = line.medium === 'hvdc' || line.tier === 1 ? 'upgrade' : 'double';
      if (!has(level, mech)) return fail('not_unlocked');
      // Fælles alarm: beredskabet betaler Planlæggerens del, så en tom kasse ikke spærrer for holdet.
      if (duoWants(state, line.id, 'plan')) return { ok: true, cost: 0 };
      return afford(state, cost);
    }
    case 'bury_line': {
      const line = target ? lineById(state, target) : undefined;
      if (!line) return fail('unknown_target');
      if (!has(level, 'cable')) return fail('not_unlocked');
      if (line.status !== 'ok') return fail('busy');
      const cost = buryCost(state, line);
      if (cost === null) return fail('not_allowed');
      return afford(state, cost);
    }
    case 'demolish': {
      const line = target ? lineById(state, target) : undefined;
      if (!line) return fail('unknown_target');
      if (line.fixed || line.owner === 'level') return fail('not_allowed');
      if (line.owner !== player) return fail('not_allowed');
      const refund = demolishRefund(state, line);
      return refund < 0 ? afford(state, -refund) : { ok: true, cost: -refund };
    }
    case 'reinforce_mast': {
      if (!has(level, 'reinforce')) return fail('not_unlocked');
      // IGN-løkke runde 2: "Forstærk alle udsatte" er ét tryk. Målet '*' tager hver luftledning i et
      // stormspor på én gang; prisen er REINFORCE_COST pr. mast.
      if (target === REINFORCE_ALL) {
        const n = exposedLines(state).length;
        if (n === 0) return fail(state.threats.some((t) => t.kind === 'storm' && t.phase === 'active') ? 'too_late' : 'not_needed');
        return afford(state, REINFORCE_COST * n);
      }
      const line = target ? lineById(state, target) : undefined;
      if (!line) return fail('unknown_target');
      if (line.medium !== 'air' || line.status !== 'ok') return fail('not_allowed');
      if (line.reinforced) return fail('already_exists');
      // Fælles alarm: Driftsvagtens del er gratis og gælder kun lynets ledning.
      if (duoWants(state, line.id, 'drift')) return { ok: true, cost: 0 };
      const atRisk = state.threats.some((t) => t.kind === 'storm' && t.phase === 'warning' && t.atRisk.includes(line.id));
      // Stormen er her allerede: for sent, ikke "kun ved stormvarsel" (playtest runde 2).
      if (!atRisk && state.threats.some((t) => t.kind === 'storm' && t.phase === 'active' && t.atRisk.includes(line.id))) return fail('too_late');
      if (!atRisk) return fail('not_needed');
      // IGN-løkke runde 2: stormberedskabet sikrer masten med det samme. Montørerne er til brud, så
      // en storm ikke længere kræver otte tryk og to nye hold (anmelderen: "Hyr en mere" to gange).
      return afford(state, REINFORCE_COST);
    }
    case 'upgrade_station': {
      if (!has(level, 'station')) return fail('not_unlocked');
      const n = target ? nodeById(state, target) : undefined;
      if (!n || !n.active || n.kind === 'foreign' || n.phantom) return fail('unknown_target');
      if (n.station === 1) return fail('already_exists');
      return afford(state, stationCost(state));
    }
    case 'build_battery': {
      if (!has(level, 'battery')) return fail('not_unlocked');
      const n = target ? nodeById(state, target) : undefined;
      if (!n || !storageNode(n)) return fail('unknown_target');
      if (n.battery) return fail('already_exists');
      return afford(state, state.perks.freeBattery > 0 ? 0 : BATTERY_COST);
    }
    case 'build_flywheel': {
      if (!has(level, 'flywheel')) return fail('not_unlocked');
      const n = target ? nodeById(state, target) : undefined;
      if (!n || !storageNode(n)) return fail('unknown_target');
      if (n.flywheels >= 2) return fail('already_exists');
      return afford(state, state.perks.freeFlywheel > 0 ? 0 : FLYWHEEL_COST);
    }
    case 'hire_crew': {
      if (!has(level, 'crew')) return fail('not_unlocked');
      if (state.crews.filter((c) => c.kind === 'van').length >= MAX_CREWS) return fail('not_allowed');
      return afford(state, CREW_COST);
    }
    case 'dispatch_crew': {
      const line = target ? lineById(state, target) : undefined;
      if (!line) return fail('unknown_target');
      if (line.status !== 'broken' && !line.halfDown) return fail('not_needed');
      if (crewOnLine(state, line.id)) return fail('busy');
      if (!nearestIdle(state, line)) return fail('no_crew');
      return { ok: true, cost: 0 };
    }
    case 'shoo_threat': {
      const t = state.threats.find((x) => x.id === target);
      // Småt-gæster kan jages væk på alle baner, også før tappet er låst op, og så længe de er der.
      if (!has(level, 'shoo') && t?.kind !== 'smaa') return fail('not_unlocked');
      if (!t) return fail('unknown_target');
      if (!isShooable(t.kind)) return fail('not_allowed');
      if (t.shooed || (t.phase !== 'warning' && !(t.kind === 'smaa' && t.phase === 'active'))) return fail('not_needed');
      return { ok: true, cost: 0 };
    }
    case 'call_year': {
      if (state.finale) return fail('not_allowed');
      // Først når årets indhold er spillet: ingen planlagte hændelser og ingen nye byer, der venter.
      if (!yearDone(state)) return fail('not_needed');
      if (state.threats.some((t) => t.kind !== 'smaa')) return fail('busy');
      if (earlyBonusPct(state) <= 0) return fail('not_needed');
      // Kun et roligt net må springe frem: ingen mørk by, intet ur over det halve, intet brud.
      if (!calmGrid(state)) return fail('busy');
      return { ok: true, cost: 0 };
    }
    case 'answer_card': {
      const t = state.threats.find((x) => x.id === target);
      if (!t) return fail('unknown_target');
      if (!t.card) return fail('not_allowed');
      if (!cardOpen(t)) return fail('not_needed');
      const o = opt as CardOption;
      if (!t.card.options.includes(o)) return fail('bad_option');
      return afford(state, CARD_COST[o]);
    }
    case 'trim_hz': {
      const why = hzBlocked(state);
      return why ? fail(why) : { ok: true, cost: 0 };
    }
    case 'power_fcr':
    case 'power_norway':
    case 'power_shed': {
      const id = powerOf(kind);
      if (!id || !has(level, id)) return fail('not_unlocked');
      if (!powerReady(state, id)) return fail('busy');
      if (id === 'shed') {
        const n = target ? nodeById(state, target) : undefined;
        if (!n || !sheddable(n)) return fail('unknown_target');
      }
      if (id === 'norway' && !norwayLanding(state)) return fail('not_allowed');
      return { ok: true, cost: 0 };
    }
    default:
      return fail('not_allowed');
  }
}

function candidateId(kind: ActionKind, target?: string, opt?: string): string {
  return `${kind}:${target ?? ''}:${opt ?? ''}`;
}

/** Alle lovlige handlinger for spilleren lige nu, i fast rækkefølge. `wait` er altid med. */
export function legalActions(state: State, player: PlayerId): Candidate[] {
  const out: Candidate[] = [];
  const push = (kind: ActionKind, target?: string, opt?: string): void => {
    const c = check(state, kind, target, opt, player);
    if (!c.ok) return;
    const cand: Candidate = { id: candidateId(kind, target, opt), kind, cost: c.cost };
    if (target !== undefined) cand.target = target;
    if (opt !== undefined) cand.opt = opt;
    out.push(cand);
  };
  if (state.phase === 'won' || state.phase === 'lost') return out;
  if (state.phase === 'yearend') {
    state.packOffer.forEach((_, i) => push('pick_pack', undefined, String(i)));
    push('pick_both');
    push('wait');
    return out;
  }
  const nodes = state.nodes.filter((n) => n.active && n.kind !== 'foreign');
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const a = nodes[i] as GridNode;
      const b = nodes[j] as GridNode;
      const media = allowedMedia(state, a, b);
      for (const m of media) push('build_line', pairKey(a.id, b.id), m);
    }
  }
  for (const l of state.lines) {
    push('upgrade_line', l.id);
    push('bury_line', l.id);
    push('dispatch_crew', l.id);
    push('reinforce_mast', l.id);
  }
  push('reinforce_mast', REINFORCE_ALL);
  for (const l of state.lines) push('demolish', l.id);
  for (const n of nodes) {
    push('upgrade_station', n.id);
    push('build_battery', n.id);
    push('build_flywheel', n.id);
  }
  push('hire_crew');
  for (const t of state.threats) push('shoo_threat', t.id);
  for (const t of state.threats) for (const o of t.card?.options ?? []) push('answer_card', t.id, o);
  for (const id of POWER_IDS) {
    if (id === 'shed') {
      for (const n of nodes) if (n.kind === 'city') push('power_shed', n.id);
    } else {
      push(powerKind(id));
    }
  }
  if (state.roles) push('trim_hz');
  push('call_year');
  push('wait');
  return out;
}

/** LYN-tilslutning: første ledning til en ny kunde inden for 10 s efter, den dukkede op, giver 3 kr. */
export const QUICK_TICKS = 100;
export const QUICK_BONUS = 3;

function newLine(state: State, a: string, b: string, medium: LineMedium, km: number, cost: number, owner: PlayerId): Line {
  return {
    id: `L${state.nextLineId++}`,
    a,
    b,
    km,
    medium,
    tier: 1 as const,
    fixed: false,
    fixedCapMw: 0,
    status: 'ok' as const,
    halfDown: false,
    reinforced: false,
    flow: 0,
    loadPm: 0,
    overTicks: 0,
    congested: false,
    builtTick: state.tick,
    spent: cost,
    owner,
  };
}

/** Udfør én intent. Fejl ændrer intet. Hændelser lægges i `events`, hvis den gives. */
export function apply(state: State, intent: Intent, events: SimEvent[] = []): ApplyResult {
  const c = check(state, intent.kind, intent.target, intent.opt, intent.player);
  if (!c.ok) return c;
  const level = getLevel(state.levelId);
  const tick = state.tick;
  const cost = c.cost;
  const from = events.length;

  switch (intent.kind) {
    case 'wait':
      break;
    case 'pick_pack': {
      const index = Number(intent.opt);
      if (!state.roles) {
        pickPack(state, level, index, false, events, intent.player);
        break;
      }
      // IGN-løkke runde 4: gaven vælges i to trin. Den første stemme sætter forslaget og giver
      // makkeren VOTE_TICKS til at sige "enig" eller vælge en anden; uenige afgøres på skift.
      const votes = { ...state.packVotes, [intent.player]: index };
      state.packVotes = votes;
      events.push({ t: 'pack_vote', tick, player: intent.player, index });
      const a = votes.p1;
      const b = votes.p2;
      if (a !== undefined && b !== undefined) {
        const who = packDecider(state);
        pickPack(state, level, a === b ? a : who === 'p1' ? a : b, false, events, a === b ? 'both' : who);
      } else state.phaseTicks = Math.max(state.phaseTicks, yearendAutoTicks(state) - VOTE_TICKS);
      break;
    }
    case 'pick_both':
      // Kassen er fælles, så den ene kan købe begge gaver uden at vente på makkerens stemme.
      state.budget -= cost;
      pickPack(state, level, 'both', false, events, state.roles || state.coop ? intent.player : undefined, cost);
      break;
    case 'build_line': {
      const [aId, bId] = splitPair(intent.target) as [string, string];
      const r = checkNewLine(state, aId, bId, intent.opt as LineMedium, () => true);
      if (!r.ok) return r;
      if (intent.opt === 'air' && state.perks.freeAir > 0) state.perks.freeAir--;
      state.budget -= cost;
      const line = newLine(state, aId, bId, r.medium, r.km, cost, intent.player);
      state.lines.push(line);
      state.stats.linesBuilt++;
      events.push({ t: 'line_built', tick, line: line.id, by: intent.player, cost });
      for (const id of [aId, bId]) {
        const n = nodeById(state, id);
        if (!n) continue;
        if (n.phantom) {
          // Fantomet forsvinder: ledningen fjernes, og halvdelen af pengene kommer tilbage (projekteringen er brugt).
          n.active = false;
          state.lines = state.lines.filter((l) => l.id !== line.id);
          state.budget += phantomRefund(cost);
          events.push({ t: 'phantom_vanished', tick, node: n.id, refund: phantomRefund(cost) });
          continue;
        }
        if (n.producer && !n.producer.feePaid) {
          n.producer.feePaid = true;
          line.feeNode = n.id;
          state.budget += n.producer.fee;
          events.push({ t: 'producer_connected', tick, node: n.id, fee: n.producer.fee });
        }
        if (n.appearedAt !== undefined && n.kind !== 'foreign' && degree(state, n.id) === 1 && state.clockTick - n.appearedAt <= QUICK_TICKS) {
          delete n.appearedAt;
          state.budget += QUICK_BONUS;
          events.push({ t: 'quick_connect', tick, node: n.id, bonus: QUICK_BONUS });
        }
      }
      updateShields(state);
      markDirty(state);
      break;
    }
    case 'upgrade_line': {
      const line = lineById(state, intent.target as string);
      if (!line) return { ok: false, error: 'unknown_target' };
      if (line.medium === 'hvdc') {
        line.fixedCapMw = 1200;
      } else {
        if (line.tier === 2 && cost === 0 && state.perks.freeDouble > 0 && !duoWants(state, line.id, 'plan')) state.perks.freeDouble--;
        line.tier = (line.tier + 1) as 2 | 3;
      }
      state.budget -= cost;
      line.spent += cost;
      line.overTicks = 0;
      markDirty(state);
      events.push({ t: 'line_upgraded', tick, line: line.id, tier: line.tier, cost });
      break;
    }
    case 'bury_line': {
      const line = lineById(state, intent.target as string);
      if (!line) return { ok: false, error: 'unknown_target' };
      line.medium = 'cable';
      line.reinforced = false;
      state.budget -= cost;
      line.spent += cost;
      events.push({ t: 'line_buried', tick, line: line.id, cost });
      break;
    }
    case 'demolish': {
      const line = lineById(state, intent.target as string);
      if (!line) return { ok: false, error: 'unknown_target' };
      const refund = -cost;
      state.budget += refund;
      if (line.feeNode && state.tick - line.builtTick <= UNDO_TICKS) {
        const p = nodeById(state, line.feeNode)?.producer;
        if (p) p.feePaid = false;
      }
      state.lines = state.lines.filter((l) => l.id !== line.id);
      for (const crew of state.crews) {
        if (crew.lineId === line.id) {
          crew.task = 'idle';
          crew.lineId = '';
          crew.route = [];
        }
      }
      for (const t of state.threats) t.atRisk = t.atRisk.filter((id) => id !== line.id);
      updateShields(state);
      markDirty(state);
      events.push({ t: 'line_removed', tick, line: line.id, refund });
      for (const n of cutNodes(state, line)) events.push({ t: 'node_cut', tick, node: n.id, kind: n.kind });
      break;
    }
    case 'reinforce_mast': {
      const lines = intent.target === REINFORCE_ALL ? exposedLines(state) : [lineById(state, intent.target as string)];
      for (const line of lines) {
        if (!line) continue;
        line.reinforced = true;
        line.spent += REINFORCE_COST;
        events.push({ t: 'mast_reinforced', tick, line: line.id });
      }
      state.budget -= cost;
      break;
    }
    case 'upgrade_station': {
      const n = nodeById(state, intent.target as string);
      if (!n) return { ok: false, error: 'unknown_target' };
      n.station = 1;
      state.budget -= cost;
      if (cost === 0 && (state.perks.freeStation ?? 0) > 0) state.perks.freeStation = (state.perks.freeStation ?? 0) - 1;
      events.push({ t: 'station_upgraded', tick, node: n.id });
      break;
    }
    case 'build_battery': {
      const n = nodeById(state, intent.target as string);
      if (!n) return { ok: false, error: 'unknown_target' };
      const big = state.perks.freeBattery > 0;
      if (big) state.perks.freeBattery--;
      const mw = big ? BIG_BATTERY_MW : BATTERY_MW;
      const storeMax = mw * BATTERY_SECONDS * TICK_HZ;
      n.battery = { mw, storeMax, store: Math.floor(storeMax / 2), usedMw: 0 };
      state.budget -= cost;
      markDirty(state);
      events.push({ t: 'battery_built', tick, node: n.id });
      break;
    }
    case 'build_flywheel': {
      const n = nodeById(state, intent.target as string);
      if (!n) return { ok: false, error: 'unknown_target' };
      if (state.perks.freeFlywheel > 0 && cost === 0) state.perks.freeFlywheel--;
      n.flywheels++;
      state.budget -= cost;
      events.push({ t: 'flywheel_built', tick, node: n.id });
      break;
    }
    case 'hire_crew': {
      state.budget -= cost;
      addCrew(state, 'van', level.crewHome, events);
      break;
    }
    case 'dispatch_crew': {
      const line = lineById(state, intent.target as string);
      const crew = line ? nearestIdle(state, line) : undefined;
      if (!line || !crew) return { ok: false, error: 'no_crew' };
      dispatch(state, crew, line, 'repair', events);
      break;
    }
    case 'shoo_threat': {
      const t = state.threats.find((x) => x.id === intent.target);
      if (!t) return { ok: false, error: 'unknown_target' };
      shoo(state, t, events);
      break;
    }
    case 'answer_card': {
      const t = state.threats.find((x) => x.id === intent.target);
      if (!t) return { ok: false, error: 'unknown_target' };
      answerCard(state, t, intent.opt as CardOption, false, events);
      break;
    }
    case 'power_fcr':
    case 'power_norway':
    case 'power_shed': {
      const id = powerOf(intent.kind);
      if (!id) return { ok: false, error: 'not_allowed' };
      const target = id === 'shed' ? (intent.target ?? '') : id === 'norway' ? (norwayLanding(state)?.id ?? '') : '';
      usePower(state, id, target, events);
      break;
    }
    case 'trim_hz':
      trimHz(state, intent.player, events);
      break;
    case 'call_year': {
      state.earlyBonusPct = earlyBonusPct(state);
      // Spring til sidste tick i året; næste step udløser årsskiftet.
      state.clockTick = (Math.floor(state.clockTick / YEAR_TICKS) + 1) * YEAR_TICKS - 1;
      // Spillet sprang frem: næste års fronter skal varsles på ny, så varslet ikke bliver kortere.
      forgetSeen(state);
      break;
    }
    default:
      return { ok: false, error: 'not_allowed' };
  }
  if (state.duo) creditDuo(state, intent, events);
  // Logbogen giver kun æren til den, der faktisk gjorde det (spor "sandhed"): hændelser fra en
  // spillers handling bærer spilleren; dem montørerne og simmen laver selv, har ingen.
  for (let i = from; i < events.length; i++) {
    const e = events[i] as SimEvent;
    if (CREDITED.has(e.t)) (e as { by?: PlayerId }).by = intent.player;
  }
  return { ok: true, cost };
}

/** Hændelser, der får spillerens navn på, når de kommer fra en handling. */
const CREDITED: ReadonlySet<SimEvent['t']> = new Set<SimEvent['t']>(['line_upgraded', 'mast_reinforced', 'station_upgraded', 'battery_built', 'flywheel_built', 'crew_dispatched', 'threat_shooed', 'card_answered']);
